import { toast } from 'react-hot-toast';
import { PostgrestError } from '@supabase/supabase-js';
import { AuthError } from '@supabase/supabase-js';
import { isNetworkError, isOffline } from './networkErrors';

export function isAuthError(error: unknown): error is AuthError {
  return error instanceof Error && 'status' in error && 'message' in error;
}

function handleSupabaseError(error: unknown): string {
  if (!error) return 'An unknown error occurred';

  // Handle auth errors
  if (isAuthError(error)) {
    switch (error.status) {
      case 401:
        return 'Please sign in to continue';
      case 403:
        return 'You do not have permission to perform this action';
      default:
        return error.message;
    }
  }

  // Handle PostgrestError
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const pgError = error as PostgrestError;
    switch (pgError.code) {
      case '23505':
        return 'This record already exists';
      case '23503':
        return 'Invalid reference to another record';
      case '42501':
        return 'You do not have permission to perform this action';
      default:
        return pgError.message || 'Database error occurred';
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

export function handleApiError(error: unknown): void {
  if (!error) return;

  // Handle offline state
  if (isOffline()) {
    toast.error('You are currently offline. Please check your internet connection.');
    return;
  }

  // Handle network errors silently (they will be retried)
  if (isNetworkError(error)) {
    return;
  }

  // Handle Supabase PostgrestError
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const pgError = error as PostgrestError;
    
    switch (pgError.code) {
      case '23505': // unique_violation
        toast.error('This record already exists');
        break;
      case '23503': // foreign_key_violation
        toast.error('Invalid reference to another resource');
        break;
      case '42501': // insufficient_privilege
        toast.error('You do not have permission to perform this action');
        break;
      default:
        console.error('Database error:', pgError);
        toast.error('An error occurred while processing your request');
    }
    return;
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    toast.error(error.message);
    return;
  }

  // Default error message
  toast.error('An unexpected error occurred. Please try again.');
}